import React, { FC, useEffect, useMemo } from "react";
import { AssignedTo } from "@green0meter/api-client";

import EntityAssignmentHeader from "./components/EntityAssignmentHeader";
import EntityAssignmentTable from "./components/EntityAssignmentTable";
import EntityAssignmentFooter from "./components/EntityAssignmentFooter";
import { getRelevantSectionsAndQuestions } from "../../FormAssignment.helpers";
import { useEntityAssignmentStore } from "./AssignmentSidePanel.store";

import { Drawer } from "@components/common";
import { useUserStore } from "@store/user";
import {
  useGetCompaniesAssignmentConfig,
  useGetFormAssignmentConfig,
  useUpdateFormAssignment,
} from "@api";
import { useFormAssignmentStore } from "@components/main/questions/QuestionsAssignment";
import { Stack } from "@components/base";

interface AssignmentSidePanel {
  formId: string;
  isOpen: boolean;
  onClose: () => void;
}

const AssignmentSidePanel: FC<AssignmentSidePanel> = ({
  formId,
  isOpen,
  onClose,
}) => {
  const { selectedSections, selectedQuestionCodes } = useFormAssignmentStore();
  const { user } = useUserStore();
  const { formAssignmentConfig } = useGetFormAssignmentConfig(user, {
    formId,
  });
  const { entitiesAssignments, setEntitiesAssignments } = useEntityAssignmentStore();
  const { updateFormAssignmentAsync } = useUpdateFormAssignment(user, formId);

  const { questionCodes, sectionCodes } = useMemo(() => {
    return getRelevantSectionsAndQuestions(
      selectedSections,
      selectedQuestionCodes,
      formAssignmentConfig,
    );
  }, [selectedSections, selectedQuestionCodes, formAssignmentConfig]);

  const { companiesAssignmentConfig, isFetchingGetCompaniesAssignmentConfig } =
    useGetCompaniesAssignmentConfig(
      user,
      {
        formId: formId,
        sectionCodes,
        questionCodes,
      },
      isOpen,
    );

  const handleSaveAssignment = async (): Promise<void> => {
    await updateFormAssignmentAsync({
      formId,
      assignedTo: AssignedTo.OnlyOwner,
      sectionCodes: sectionCodes,
      questionCodes: questionCodes,
      companyAssignments: entitiesAssignments.map((assignment) => ({
        companyId: assignment.id,
        assignedTo: assignment.assignedTo,
        userIds: assignment.assignedUsers?.map((user) => user.id) ?? [],
      })),
    });
    onClose();
  };

  useEffect(() => {
    setEntitiesAssignments(companiesAssignmentConfig ?? []);
  }, [companiesAssignmentConfig]);

  return (
    <Drawer
      open={isOpen}
      onClose={onClose}
      content={
        <Stack height="100%" spacing={2} justifyContent="space-between">
          <Stack spacing={2}>
            <EntityAssignmentHeader onClose={onClose} />
            <EntityAssignmentTable
              isLoading={isFetchingGetCompaniesAssignmentConfig}
            />
          </Stack>
          <EntityAssignmentFooter onCancel={onClose} onSave={handleSaveAssignment} />
        </Stack>
      }
    />
  );
};

export default AssignmentSidePanel;
